import { createHtmlField, createSelectField, createTextField, NumericAutocompleteField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useMemo, useRef, useState } from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Box, Grid, InputLabel, TextField, Typography } from '@material-ui/core';
import { TaskPriorityField } from '../../editTask/taskPriorityField';
import { defaultSectionName, generalTaskPriorityOptions, taskTypeOptions } from '../../../utils/generalTasksConstants';
import { GeneralTasksStore } from '../../../stores/generalTasksStore';
import { TaskAssigneeField } from '../../editTask/taskAssigneeField';
import { TaskWatchField } from '../../editTask/taskWatchField';
import { useSearchBarStore } from '@roc/feature-app-core';
import { Autocomplete } from '@material-ui/lab';
import { Search } from '@material-ui/icons';
import { debounce } from '@roc/feature-utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    inputLabel: {
      marginBottom: theme.spacing(1),
      color: '#222222',
    }
  }),
);

type NonLoanTaskFormProps = {
  generalTasksStore: GeneralTasksStore;
};

export const NonLoanTaskForm = observer(({
  generalTasksStore,
}: NonLoanTaskFormProps) => {
  const inputRef = useRef<HTMLInputElement>();
  const { editTaskStore, globalStore, assigneeOptions, watcherOptions } = generalTasksStore;
  const isInternal = globalStore.isInternalPortal || globalStore.isLoanChatApp;

  const { nonLoanTaskFormStore } = editTaskStore;

  const isLoanView = !!generalTasksStore.loanId;
  const { searchBarStore } = useSearchBarStore();
  const classes = useStyles();

  const fetchOptions = useMemo(
    () => debounce(searchText => editTaskStore.fetchOptions(searchText), 500),
    [editTaskStore]
  );

  const onSelect = (val) => {
    nonLoanTaskFormStore.onFieldChange("loanId", val?.loanId);
  }

  const SearchOption = ({ option }) => {
    return (
      <Box overflow={'hidden'} width={'100%'} p={1}>
        <Box flex={1}>
          <Typography>
            <b>{option.loanId}</b> - {option.dealName}
          </Typography>
        </Box>
        <Typography>{option.lender}</Typography>
      </Box>
    );
  };
  const search = useMemo(
    () =>
      debounce(value => {
        if (value.length >= 3) {
          searchBarStore.searchLoans(value);
        } else {
          searchBarStore.reset();
        }
      }, 500),
    []
  );

  const [inputValue, setInputValue] = useState("");

  return (
    <>
      <Grid container spacing={2} innerRef={inputRef}>
        {!isLoanView && <Grid item xs={12}>
          <InputLabel
            htmlFor="menu-appbar"
            className={classes.inputLabel}
          >
            Search Loan
          </InputLabel>
          <Autocomplete
            fullWidth
            id="menu-appbar"
            options={searchBarStore.searchResults}
            componentName="menu-appbar"
            inputValue={inputValue}
            renderInput={options => (
              <TextField
                {...options}
                autoFocus
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...options.InputProps,
                  startAdornment:
                    inputValue.length === 0 ? <Search /> : <></>,
                }}
              />
            )}
            renderOption={option => <SearchOption option={option} />}
            blurOnSelect={true}
            getOptionLabel={(option: any) => option.loanId.toString()}
            loading={searchBarStore.loading}
            filterOptions={options => options}
            autoSelect={false}
            onChange={(e, value) => {
              onSelect(value);
            }}
            onInputChange={(e, value) => {
              setInputValue(value);
              search(value);
            }}
          />
        </Grid>}
        {
          isLoanView && <Grid item xs={12}>
            <NumericAutocompleteField
              label="Loan Id"
              placeHolder="Select a Loan Id"
              value={nonLoanTaskFormStore.form.fields.loanId.value}
              options={editTaskStore.loanSearchOptions}
              onChange={text => {
                fetchOptions(text);
              }}
              onSelect={opt => {
                if (!isInternal) {
                  generalTasksStore.fetchLoanActorsAssigneeOptions(Number(opt.value));
                }
                editTaskStore.fetchLoanTaskSectionNames(Number(opt.value), 'Underwriting');
                nonLoanTaskFormStore.onFieldChange("loanId", opt.value);
              }}
              disabled={isLoanView}
            />
          </Grid>
        }
        <Grid item xs={12}>
          <TaskAssigneeField
            label={'Assignee'}
            required
            value={nonLoanTaskFormStore.form.fields.assigneeId.value}
            onChange={(e, value) => nonLoanTaskFormStore.onFieldChange('assigneeId', value)}
            assigneeOptions={assigneeOptions}
            disabled={assigneeOptions?.length === 0}
            error={nonLoanTaskFormStore.form.fields.assigneeId.error}
          />
        </Grid>
        <Grid item xs={12}>
          {createSelectField({
            options: taskTypeOptions,
            label: 'Category',
            store: nonLoanTaskFormStore,
            fieldName: 'category',
            testId: 'category',
          })}
        </Grid>
        <Grid item xs={12}>
          <TaskWatchField
            label={'Watchers'}
            value={nonLoanTaskFormStore.form.fields.watchIds.value || []}
            onChange={(event, value) => nonLoanTaskFormStore.onFieldChange('watchIds', value)}
            assigneeOptions={watcherOptions}
            disabled={watcherOptions?.length === 0}
          />
        </Grid>
        {isInternal && (
          <Grid item xs={12}>
            <TaskPriorityField
              label={'Priority'}
              required
              value={nonLoanTaskFormStore.form.fields.priority.value}
              onChange={(e, value) => nonLoanTaskFormStore.onFieldChange('priority', value)}
              options={generalTaskPriorityOptions}
              disabled={false}
              error={nonLoanTaskFormStore.form.fields.priority.error}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          {createTextField({
            store: nonLoanTaskFormStore,
            testId: 'title',
            fieldName: 'title',
            label: 'Title',
            ref: inputRef,
          })}
        </Grid>
        <Grid item xs={12}>
          {createHtmlField({
            store: nonLoanTaskFormStore,
            testId: 'description',
            fieldName: 'description',
            label: 'Description',
            multiline: true,
            minRows: 2,
          })}
        </Grid>
      </Grid>
    </>

  );
});
